import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { FaEdit, FaTrash } from 'react-icons/fa';

const Note = ({ applicationId, userName, userEmail, candidateName, setIsNoteOpen }) => {
    const BASE_API_URL = process.env.REACT_APP_API_URL;
    const [notes, setNotes] = useState([]);  // State to store list of notes
    const [message, setMessage] = useState('');
    const [editingNoteId, setEditingNoteId] = useState();  // To track which note is being edited
    const notesContainerRef = useRef(null);  // Correcting scroll ref

    useEffect(() => {
        if (applicationId) {
            fetchNotes();
        }
    }, [applicationId]);

    const fetchNotes = async () => {
        try {
            const response = await axios.get(`${BASE_API_URL}/getNotes`, {
                params: { applicationId }
            });
            setNotes(response.data || []);  // Store the list of notes
        } catch (error) {
        }
    };

    // Handle changes to the message input
    const handleMessageChange = (event) => {
        setMessage(event.target.value);
    };

    const handleNoteChange = async () => {
        const payload = {
            note: message,
            userName,
            userEmail,
            applicationId,
        };

        if (editingNoteId) {
            // Update existing note
            try {
                await axios.put(`${BASE_API_URL}/updateNote?noteId=${editingNoteId}&note=${message}`);
                setMessage('');
                setEditingNoteId();  // Clear editing state
                fetchNotes();  // Refresh notes after update
            } catch (error) {
            }
        } else {
            // Add new note
            try {
                await axios.post(`${BASE_API_URL}/postNote`, payload);
                setMessage('');
                fetchNotes();  // Refresh notes after adding
            } catch (error) {
            }
        }
    };

    // Handle editing a note
    const handleEditNote = (noteId, noteText) => {
        setEditingNoteId(noteId);
        setMessage(noteText);  // Pre-fill the message with the note's text
    };

    // Handle deleting a note
    const handleDeleteNote = async (noteId) => {
        try {
            await axios.delete(`${BASE_API_URL}/deleteNote?noteId=${noteId}`);
            fetchNotes();  // Refresh notes after deletion
        } catch (error) {
        }
    };

    const handleClose = () => {
        setIsNoteOpen(false);
    };

    // Scroll to the bottom whenever the notes change
    useEffect(() => {
        if (notesContainerRef.current) {
            notesContainerRef.current.scrollTop = notesContainerRef.current.scrollHeight;
        }
    }, [notes]);

    const renderNotes = () => {
        return notes.map((note) => (
            <div key={note.noteId} className="mb-3 shadow-sm rounded p-3">
                <div className="fw-bold">{note.userName}</div>
                <div style={{ whiteSpace: 'pre-line', wordWrap: 'break-word' }}>
                    {note.note || 'No content'}
                </div>

                {/* Conditional rendering of the Edit and Delete buttons */}
                {note.userEmail === userEmail && (
                    <div className="d-flex justify-content-start mt-3">
                        <Button
                            variant="warning"
                            onClick={() => handleEditNote(note.noteId, note.note)}
                            aria-label="Edit Note"
                            className="me-2"
                        >
                            <FaEdit />
                        </Button>

                        <Button
                            variant="danger"
                            onClick={() => handleDeleteNote(note.noteId)}
                            aria-label="Delete Note"
                        >
                            <FaTrash />
                        </Button>
                    </div>
                )}
            </div>
        ));
    };

    return (
        <Modal show={true} onHide={handleClose} centered >
            <Modal.Header className="fw-bold text-dark" closeButton>
                Add a Note for {candidateName}
            </Modal.Header>

            <Modal.Body
                style={{
                    maxHeight: '400px',  // Set the max height to 500px
                    overflowY: 'auto',  // Enable vertical scrolling if content exceeds maxHeight
                }}
                ref={notesContainerRef}  // Correctly attach the ref
            >
                {/* Display all notes */}
                {renderNotes()}
            </Modal.Body>

            {/* Input section for adding/updating the note */}
            <Modal.Footer>
                <Form>
                    <Form.Group controlId="note" className="mb-3">
                        <Form.Label>Note</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={6}
                            value={message}
                            onChange={handleMessageChange}
                            placeholder="Write your note here..."
                            className="p-3 border rounded"
                            style={{
                                backgroundColor: "#f8f9fa",
                                resize: "none",
                            }}
                        />
                    </Form.Group>
                </Form>

                {/* Button section */}
                <div className="d-flex justify-content-between mt-3">
                    <Button
                        variant="primary"
                        onClick={handleNoteChange}
                        className="px-4 py-2 shadow-sm"
                    >
                        {editingNoteId ? 'Save Changes' : 'Add Note'}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default Note;
